import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, Gift, QrCode, Phone, Mail, Calendar, MapPin, TrendingUp, Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useAuth } from '@/hooks/useAuth'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface CustomerDetail {
  id: string
  full_name: string
  email: string
  phone?: string
  points: number
  total_visits: number
  visit_stamps: number
  referral_stamps: number
  total_referrals: number
  last_visit?: string
  created_at: string
  referral_code: string
  referred_by?: string
}

interface CustomerStamp {
  id: string
  stamp_type: string
  points_awarded: number
  created_at: string
  created_by?: string
}

interface CustomerRedemption {
  id: string
  reward_name: string
  redemption_code: string
  stamps_used: number
  status: string
  created_at: string
  used_at?: string
}

const CustomerDetail = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { tenantId } = useAuth()
  const { toast } = useToast()

  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [stamps, setStamps] = useState<CustomerStamp[]>([])
  const [redemptions, setRedemptions] = useState<CustomerRedemption[]>([])
  const [loading, setLoading] = useState(true)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false)
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: ''
  })
  const [rewardPoints, setRewardPoints] = useState(1)

  useEffect(() => {
    if (customerId && tenantId) {
      fetchCustomerDetail()
    }
  }, [customerId, tenantId])

  const fetchCustomerDetail = async () => {
    if (!customerId || !tenantId) return

    try {
      setLoading(true)

      // Fetch customer details
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .eq('tenant_id', tenantId)
        .single()

      if (customerError) throw customerError

      setCustomer(customerData)
      setEditForm({
        full_name: customerData.full_name,
        email: customerData.email,
        phone: customerData.phone || ''
      })

      // Fetch stamps
      const { data: stampsData, error: stampsError } = await supabase
        .from('stamps')
        .select('*')
        .eq('customer_id', customerId)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })

      if (stampsError) throw stampsError
      setStamps(stampsData || [])

      // Fetch redemptions
      const { data: redemptionsData, error: redemptionsError } = await supabase
        .from('reward_redemptions')
        .select(`
          id,
          redemption_code,
          stamps_used,
          status,
          created_at,
          used_at,
          rewards (
            name
          )
        `)
        .eq('customer_id', customerId)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })

      if (redemptionsError) throw redemptionsError
      
      const transformedRedemptions = (redemptionsData || []).map(r => ({
        id: r.id,
        reward_name: (r.rewards as any)?.name || 'Unbekannte Belohnung',
        redemption_code: r.redemption_code,
        stamps_used: r.stamps_used,
        status: r.status,
        created_at: r.created_at,
        used_at: r.used_at
      }))
      
      setRedemptions(transformedRedemptions)

    } catch (error: any) {
      console.error('Error fetching customer detail:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Kundendaten konnten nicht geladen werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateCustomer = async () => {
    if (!customer || !tenantId) return

    try {
      const { error } = await supabase
        .from('customers')
        .update({
          full_name: editForm.full_name.trim(),
          email: editForm.email.toLowerCase().trim(),
          phone: editForm.phone.trim() || null
        })
        .eq('id', customer.id)

      if (error) throw error

      toast({
        title: "Kunde aktualisiert",
        description: "Kundendaten wurden erfolgreich aktualisiert."
      })

      setIsEditModalOpen(false)
      fetchCustomerDetail()

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Kunde konnte nicht aktualisiert werden."
      })
    }
  }

  const handleDeleteCustomer = async () => {
    if (!customer || !tenantId) return

    try {
      // Delete stamps first
      await supabase
        .from('stamps')
        .delete()
        .eq('customer_id', customer.id)

      // Delete redemptions
      await supabase
        .from('reward_redemptions')
        .delete()
        .eq('customer_id', customer.id)

      // Delete customer
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customer.id)

      if (error) throw error

      toast({
        title: "Kunde gelöscht",
        description: "Kunde wurde erfolgreich gelöscht."
      })

      navigate('/admin/customers')

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Kunde konnte nicht gelöscht werden."
      })
    }
  }

  const handleAddStamp = async () => {
    if (!customer || !tenantId) return

    try {
      // Add stamp
      const { error: stampError } = await supabase
        .from('stamps')
        .insert({
          tenant_id: tenantId,
          customer_id: customer.id,
          points_awarded: 1,
          stamp_type: 'visit'
        })

      if (stampError) throw stampError

      // Update customer
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          points: customer.points + 1,
          visit_stamps: customer.visit_stamps + 1,
          total_visits: customer.total_visits + 1,
          last_visit: new Date().toISOString()
        })
        .eq('id', customer.id)

      if (updateError) throw updateError

      toast({
        title: "Stempel vergeben",
        description: "Besuchsstempel wurde erfolgreich vergeben."
      })

      fetchCustomerDetail()

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Stempel konnte nicht vergeben werden."
      })
    }
  }

  const handleAwardReward = async () => {
    if (!customer || !tenantId) return

    try {
      // Add reward stamp
      const { error: stampError } = await supabase
        .from('stamps')
        .insert({
          tenant_id: tenantId,
          customer_id: customer.id,
          points_awarded: rewardPoints,
          stamp_type: 'reward'
        })

      if (stampError) throw stampError

      // Update customer points
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          points: customer.points + rewardPoints
        })
        .eq('id', customer.id)

      if (updateError) throw updateError

      toast({
        title: "Belohnung vergeben",
        description: `${rewardPoints} Bonuspunkte wurden erfolgreich vergeben.`
      })

      setIsRewardModalOpen(false)
      setRewardPoints(1)
      fetchCustomerDetail()

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Belohnung konnte nicht vergeben werden."
      })
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-center">
            <p>Wird geladen...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!customer) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-6 py-8">
          <Card>
            <CardHeader>
              <CardTitle>Kunde nicht gefunden</CardTitle>
              <CardDescription>
                Der angeforderte Kunde konnte nicht gefunden werden.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/customers')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zur Liste
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{customer.full_name}</h1>
              <p className="text-muted-foreground">{customer.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button onClick={handleAddStamp}>
              <QrCode className="h-4 w-4 mr-2" />
              Stempel vergeben
            </Button>
            <Button variant="outline" onClick={() => setIsRewardModalOpen(true)}>
              <Gift className="h-4 w-4 mr-2" />
              Belohnung
            </Button>
            <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Bearbeiten
            </Button>
            <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Löschen
            </Button>
          </div>
        </div>

        {/* Customer Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="flex items-center p-6">
              <Heart className="h-8 w-8 text-primary mr-3" />
              <div>
                <p className="text-2xl font-bold">{customer.visit_stamps}</p>
                <p className="text-sm text-muted-foreground">Besuchsstempel</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Gift className="h-8 w-8 text-accent mr-3" />
              <div>
                <p className="text-2xl font-bold">{customer.referral_stamps}</p>
                <p className="text-sm text-muted-foreground">Empfehlungen</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <TrendingUp className="h-8 w-8 text-success mr-3" />
              <div>
                <p className="text-2xl font-bold">{customer.total_visits}</p>
                <p className="text-sm text-muted-foreground">Gesamtbesuche</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Calendar className="h-8 w-8 text-warning mr-3" />
              <div>
                <p className="text-2xl font-bold">{customer.points}</p>
                <p className="text-sm text-muted-foreground">Gesamtpunkte</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Information */}
        <Tabs defaultValue="info" className="space-y-6">
          <TabsList>
            <TabsTrigger value="info">Informationen</TabsTrigger>
            <TabsTrigger value="stamps">Stempelhistorie</TabsTrigger>
            <TabsTrigger value="redemptions">Einlösungen</TabsTrigger>
          </TabsList>
          
          <TabsContent value="info">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Kontaktdaten</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{customer.email}</span>
                  </div>
                  {customer.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Mitglied seit {format(new Date(customer.created_at), 'dd.MM.yyyy', { locale: de })}
                    </span>
                  </div>
                  {customer.last_visit && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>
                        Letzter Besuch: {format(new Date(customer.last_visit), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Empfehlungscode</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-muted rounded-lg">
                    <code className="text-lg font-mono">{customer.referral_code}</code>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Empfehlungen: {customer.total_referrals}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="stamps">
            <Card>
              <CardHeader>
                <CardTitle>Stempelhistorie</CardTitle>
                <CardDescription>
                  Alle vergebenen Stempel und Prämien
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Typ</TableHead>
                      <TableHead>Punkte</TableHead>
                      <TableHead>Datum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stamps.map((stamp) => (
                      <TableRow key={stamp.id}>
                        <TableCell>
                          <Badge variant={stamp.stamp_type === 'visit' ? "default" : "secondary"}>
                            {stamp.stamp_type === 'visit' ? 'Besuch' : 
                             stamp.stamp_type === 'referral' ? 'Empfehlung' : 'Belohnung'}
                          </Badge>
                        </TableCell>
                        <TableCell>+{stamp.points_awarded}</TableCell>
                        <TableCell>
                          {format(new Date(stamp.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="redemptions">
            <Card>
              <CardHeader>
                <CardTitle>Eingelöste Prämien</CardTitle>
                <CardDescription>
                  Alle eingelösten Prämien und deren Status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prämie</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Stempel verwendet</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Eingelöst am</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {redemptions.map((redemption) => (
                      <TableRow key={redemption.id}>
                        <TableCell>{redemption.reward_name}</TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm">
                            {redemption.redemption_code}
                          </code>
                        </TableCell>
                        <TableCell>{redemption.stamps_used}</TableCell>
                        <TableCell>
                          <Badge variant={
                            redemption.status === 'used' ? "default" :
                            redemption.status === 'pending' ? "secondary" : "outline"
                          }>
                            {redemption.status === 'used' ? 'Verwendet' :
                             redemption.status === 'pending' ? 'Ausstehend' : 'Abgelaufen'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(redemption.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Kunde bearbeiten</DialogTitle>
              <DialogDescription>
                Bearbeiten Sie die Kundendaten.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="full_name">Name</Label>
                <Input
                  id="full_name"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                />
              </div>
              
              <div>
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleUpdateCustomer}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reward Modal */}
        <Dialog open={isRewardModalOpen} onOpenChange={setIsRewardModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Belohnung vergeben</DialogTitle>
              <DialogDescription>
                Vergeben Sie Bonuspunkte an den Kunden.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="reward_points">Anzahl Punkte</Label>
                <Input
                  id="reward_points"
                  type="number"
                  min="1"
                  value={rewardPoints}
                  onChange={(e) => setRewardPoints(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRewardModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleAwardReward}>
                Belohnung vergeben
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kunde löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie {customer.full_name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                Alle Stempel und Einlösungen des Kunden werden ebenfalls gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteCustomer}>
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  )
}

export default CustomerDetail